import React, { CSSProperties, Fragment, ReactNode } from 'react';
import {
  getResponsiveStylesForProperties,
  ResponsiveCssProperties,
  ResponsiveCssVariables,
} from '../_internal/responsiveStyling';
import type { NonResponsiveValue } from '../_internal/styling';
import useUniqueId from '../_internal/useUniqueId';

const CLASS_NAME_PREFIX = 'mrrp-'; // (translation: mode-react-responsive-prop)

interface RenderPropParams {
  /**
   * The unique class name that should be added directly to the rendered
   * element/component's `className`. The class name is referenced in any media
   * queries within `<style>` elements
   */
  className: string;

  /**
   * Any inline styles for non-responsive props that don't have any media
   * queries. These should be added/merged directly to the `style` of the
   * rendered element/component.
   */
  style: CSSProperties;
}

export interface ResponsiveBaseProps<
  OptionsValue extends ResponsiveCssProperties<NonResponsiveValue>,
> {
  /**
   * A render prop that renders the actual UI, given `className` & `style` props
   */
  children: (params: RenderPropParams) => ReactNode;

  /**
   * A look-up of CSS properties to responsive prop configurations
   */
  properties: OptionsValue;

  /**
   * A look-up of CSS variables (eg `'--my-variable'`) to responsive prop configurations
   */
  variables?: ResponsiveCssVariables;
}

/**
 * The `ResponsiveBase` component is used as a base render prop component to
 * convert a component's responsive props into CSS media queries so that the responsive styles
 * can display correctly on pre-rendered or server-side rendered pages.
 */
const ResponsiveBase = <
  OptionsValue extends ResponsiveCssProperties<NonResponsiveValue>,
>({
  children,
  properties,
  variables = {},
}: ResponsiveBaseProps<OptionsValue>) => {
  const uniqueClassName = useUniqueId(CLASS_NAME_PREFIX);
  const { cssStyles, inlineStyles } = getResponsiveStylesForProperties(
    uniqueClassName,
    { ...properties, ...variables },
  );
  const hasCssStyles = cssStyles.length > 0;

  return (
    <>
      {
        // writing a `<style>` for each so that if the component is re-rendered
        // with changed `properties` we only have to re-render the changes
        cssStyles.map(cssStyle => (
          <style
            key={cssStyle}
            data-testid="responsive-base-style"
            data-test-for={uniqueClassName}
          >
            {cssStyle}
          </style>
        ))
      }
      <Fragment key={uniqueClassName}>
        {children({
          // if there are no media query styles there's no need to add the `className`
          className: hasCssStyles ? uniqueClassName : '',

          style: inlineStyles,
        })}
      </Fragment>
    </>
  );
};

export default ResponsiveBase;
